import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import FinalResultsCharts from "../components/ResultsChart/FinalResultsCharts";
import abbottLogo from '../images/abbott_logo.png';

function EndPresentation() {
  const { sessionId } = useParams(); 
  const [searchParams] = useSearchParams();
  
  const [presentationData, setPresentationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const targetSessionId = sessionId || "default"; 

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/sessions/${targetSessionId}/summary`);

        if (!response.ok) {
          throw new Error('Failed to fetch presentation summary');
        }

        const data = await response.json();
        setPresentationData(data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-[0.8vw] text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h1 className="text-[0.8vw] font-bold text-red-600 mb-4">Error Loading Results</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!presentationData) return null;

  const { presentation_title, city, summary, results } = presentationData;

  return (
    <div>
      <div className='flex justify-center mx-[1vw] my-[1vw]'>
        <img src={abbottLogo} alt="Abbott Logo" className='h-[3vw]' />
      </div>
      <h1 className="text-[1.4vw] font-bold text-center mt-10">
        {presentation_title || "Presentation"} Ended
      </h1>
      {city && (
          <h2 className="text-[1.2vw] font-semibold text-center mt-2 text-gray-600">
              {city}
          </h2>
      )}
      <p className="text-center mt-4 text-gray-500 font-medium text-[1.1vw]">Thank you for Attending!</p>

      {summary && (
        <div className="text-center mt-[1vw] mb-[1vw]">
          <div className="bg-blue-50 rounded-[0.5vw] p-[1vw] max-w-[60vw] mx-auto">
            <h2 className="text-[1vw] font-semibold text-blue-800 mb-[1vw]">Presentation Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-[1vw] justify-center items-center text-center">
              <div className="bg-white rounded-[0.5vw] p-[1vw] shadow-sm">
                <div className="text-[1.1vw] font-bold text-blue-600">{summary.total_questions || 0}</div>
                <div className="text-[0.8vw] text-gray-600">Total Questions</div>
              </div>
              <div className="bg-white rounded-[0.5vw] p-[1vw] shadow-sm">
                <div className="text-[1.1vw] font-bold text-green-600">{summary.questions_answered || 0}</div>
                <div className="text-[0.8vw] text-gray-600">Questions Answered</div>
              </div>
              <div className="bg-white rounded-[0.5vw] p-[1vw] shadow-sm">
                <div className="text-[1.1vw] font-bold text-purple-600">{summary.total_responses || 0}</div>
                <div className="text-[0.8vw] text-gray-600">Total Responses</div>
              </div>
              <div className="bg-white rounded-[0.5vw] p-[1vw] shadow-sm">
                <div className="text-[1.1vw] font-bold text-orange-600">{summary.unique_attendees || 0}</div>
                <div className="text-[0.8vw] text-gray-600">Unique Attendees</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <p className="text-center mt-4 text-[1.2vw] font-semibold text-lightPurple">
        Attendee Results Overview
      </p>

      {results && results.length > 0 ? (
        <FinalResultsCharts questions={results.map(r => ({
            id: r.slide_id,
            question: r.question_text,
            type: r.type,
            options: r.options.map(o => o.choice_text || o.label || o), 
            results: r.options.map(o => o.count || 0) 
        }))} />
      ) : (
        <div className="text-center mt-10">
          <p className="text-gray-500 text-[0.8vw]">No responses received yet.</p>
        </div>
      )}
    </div>
  );
}

export default EndPresentation;
